import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon, 
  UserCircleIcon,
  ChevronDownIcon,
  FireIcon,
  RectangleStackIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { USER_ROLES } from '../models/dataModels';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { currentUser, userProfile, currentRole, logout } = useAuth();
  const location = useLocation();

  const navigation = currentUser ? [
    { name: 'Play', href: '/', icon: FireIcon, pattern: /^\/$|^\/player\// },
  ] : [];

  const roleBasedNavigation = {
    [USER_ROLES.LEAGUE_MANAGER]: [
      { name: 'Manage Leagues', href: '/manage-leagues', icon: RectangleStackIcon, pattern: /^\/manage-leagues/ },
    ],
    [USER_ROLES.ADMIN]: [
      { name: 'Admin', href: '/admin', icon: CogIcon, pattern: /^\/admin/ },
    ],
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsProfileOpen(false);
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };


  const isActive = (item) => {
    // If item has a pattern, check if pathname matches the pattern
    if (item.pattern) {
      return item.pattern.test(location.pathname);
    }
    // Otherwise, check for exact match
    return location.pathname === item.href;
  };

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and main navigation */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-survivor-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">🏝️</span>
                </div>
                <span className="text-xl font-bold text-gray-900">Survivor League</span>
              </Link>
            </div>
            
            {/* Desktop navigation */}
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActive(item)
                      ? 'border-survivor-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  <item.icon className="w-4 h-4 mr-1" />
                  {item.name}
                </Link>
              ))}
              
              {/* Role-based navigation - show all tabs for user's roles */}
              {currentUser && (() => {
                const allNavItems = [];
                userProfile?.roles?.forEach(role => {
                  const roleItems = roleBasedNavigation[role] || [];
                  roleItems.forEach(item => {
                    if (!allNavItems.find(existing => existing.href === item.href)) {
                      allNavItems.push(item);
                    }
                  });
                });
                return allNavItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isActive(item)
                        ? 'border-survivor-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    <item.icon className="w-4 h-4 mr-1" />
                    {item.name}
                  </Link>
                ));
              })()}
            </div>
          </div>

          {/* User menu */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            {currentUser ? (
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-2 bg-white rounded-full p-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500"
                >
                  <UserCircleIcon className="w-8 h-8 text-gray-400" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{userProfile?.displayName || currentUser.email}</p>
                    <p className="text-xs text-gray-500 capitalize">{currentRole.replace(/([A-Z])/g, ' $1').trim()}</p>
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                </button>

                {/* Profile dropdown */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      Profile Settings
                    </Link>
                    
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex space-x-4">
                <Link
                  to="/login"
                  className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="bg-survivor-500 hover:bg-survivor-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-survivor-500"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                  isActive(item)
                    ? 'bg-survivor-50 border-survivor-500 text-survivor-700'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
                }`}
                onClick={() => setIsOpen(false)}
              >
                <div className="flex items-center">
                  <item.icon className="w-5 h-5 mr-2" />
                  {item.name}
                </div>
              </Link>
            ))}
            
            {/* Role-based navigation for mobile */}
            {currentUser && (() => {
              const allNavItems = [];
              userProfile?.roles?.forEach(role => {
                const roleItems = roleBasedNavigation[role] || [];
                roleItems.forEach(item => {
                  if (!allNavItems.find(existing => existing.href === item.href)) {
                    allNavItems.push(item);
                  }
                });
              });
              return allNavItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                    isActive(item)
                      ? 'bg-survivor-50 border-survivor-500 text-survivor-700'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <div className="flex items-center">
                    <item.icon className="w-5 h-5 mr-2" />
                    {item.name}
                  </div>
                </Link>
              ));
            })()}

            {/* Sign in / Sign up for mobile (when not logged in) */}
            {!currentUser && (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/login"
                  className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                  onClick={() => setIsOpen(false)}
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="block pl-3 pr-4 py-2 mx-3 my-2 text-base font-medium text-white bg-survivor-500 hover:bg-survivor-600 rounded-md text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Sign up
                </Link>
              </>
            )}

            {/* Profile Settings and Sign Out for mobile */}
            {currentUser && (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/profile"
                  className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                    location.pathname === '/profile'
                      ? 'bg-survivor-50 border-survivor-500 text-survivor-700'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <div className="flex items-center">
                    <UserCircleIcon className="w-5 h-5 mr-2" />
                    Profile Settings
                  </div>
                </Link>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    handleLogout();
                  }}
                  className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                >
                  Sign out
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );

}
